<template>
  <BaseModal
    small
    @close="emit('close')"
    useForm
    @submit="handleMutation"
  >
    <div :class="$style.base">
      <BaseGrid
        :size="7"
        center
        alignCenter
        :mt="1.5"
      >
        <div :class="$style.logo">
          <BaseLogo fullVersion />
        </div>
        <BaseDropdown
          v-model="state.salonizedEmployeeId"
          :loading="employeesLoading"
          :label="$t('onboarding.demo_by')"
          :options="
            employees.map((e: any) => ({
              value: e.id,
              label: e.name,
              avatar: { picture: e.profileImage }
            }))
          "
          required
          mt
          v-test="'demoOnboardingDropdown'"
        />
        <BaseInput
          v-model="state.name"
          :label="$t('onboarding.name')"
          required
          mt
          v-test="'onboardingNameInput'"
        />
        <BaseInput
          v-model="state.email"
          type="email"
          :label="$t('signup.email')"
          mt
          required
          :error="state.emailInUse ? $t('signup.errors.email_in_use') : null"
          v-test="'onboardingEmailInput'"
        />
        <BaseInput
          v-model="state.phone"
          type="tel"
          :minLength="9"
          :maxLength="16"
          :label="$t('signup.phone')"
          mt
          v-test="'onboardingPhoneInput'"
        />
        <BaseInput
          v-model="state.trialDays"
          type="number"
          :minValue="0"
          :label="$t('onboarding.trial_days')"
          mt
          v-test="'onboardingTrialDaysInput'"
        />
        <BaseButton
          fullWidth
          :mt="2"
          :mb="1"
          size="l"
          :loading="loading"
          submitForm
          v-test="'onBoardingCreate'"
        >
          {{ $t('onboarding.start_demo') }}
        </BaseButton>
        <BaseText
          size="s"
          center
        >
          {{ $t('onboarding.demo_terms') }}
        </BaseText>
      </BaseGrid>
    </div>
  </BaseModal>
</template>

<script
  lang="ts"
  setup
>
import { useMutation, useQuery } from '@vue/apollo-composable';
import { useStorage } from '@vueuse/core';
import gql from 'graphql-tag';
import { computed, reactive } from 'vue';
import { useRouter } from 'vue-router';
import { redirectRoute } from '@/router/helpers';
import { useSessionStore } from '@/stores/session';

const emit = defineEmits(['close']);

const router = useRouter();

const state = reactive({
  salonizedEmployeeId: '',
  name: '',
  email: '',
  phone: '',
  trialDays: 14,
  emailInUse: false,
});

const {
  result,
  loading: employeesLoading,
  onResult,
} = useQuery(gql`
  query salonizedEmployees {
    salonizedEmployees {
      name
      id
      profileImage
      isLastDemoGiver
    }
  }
`);

const employees = computed(() => result.value?.salonizedEmployees || []);
onResult(() => {
  state.salonizedEmployeeId = useStorage('last_employee_id', null);
});

const { mutate: createTradeShowAccount, loading } = useMutation(gql`
  mutation createTradeShowTrial($input: CreateTradeShowTrialInput!) {
    createTradeShowTrial(input: $input) {
      company {
        name
        id
        primaryLocationId
      }
      errors {
        attribute
        message
        type
      }
    }
  }
`);

const handleMutation = () => {
  createTradeShowAccount({
    input: {
      salonizedEmployeeId: state.salonizedEmployeeId,
      name: state.name,
      email: state.email,
      phone: state.phone,
      trialDays:
        state.trialDays && state.trialDays !== 14
          ? Number(state.trialDays)
          : null,
    },
  }).then((response: any) => {
    const emailTaken = response.data.createTradeShowTrial.errors?.some(
      (e: any) => e.type === 'taken' && e.attribute === 'email',
    );

    if (emailTaken) {
      state.emailInUse = true;
    } else {
      const { fetchAllData } = useSessionStore();
      fetchAllData().then(() => {
        emit('close');
        router.push(redirectRoute());
      });
    }
  });
};
</script>

<style
  lang="scss"
  module
>
.base {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  margin-top: $spacing;
}
</style>
